/**
 * Widget generator + embed BFF (Kong: /api/v1/widget-generator, /api/v1/embed).
 *
 * `tenantApiFetch` handles `Authorization: Bearer` and `X-Workspace-Id` (the
 * authoritative workspace selector). Body fields here never duplicate those.
 */

import { tenantApiFetch } from "@/lib/tenantApi";

/**
 * @param {Record<string, unknown>} [body] — optional `{ widget_defaults: { ... } }`
 */
export async function postWidgetBootstrap(body = {}) {
  return tenantApiFetch("/api/v1/widget-generator/bootstrap", {
    method: "POST",
    body: JSON.stringify(body && Object.keys(body).length ? body : {}),
  });
}

export async function getWidgetSettings() {
  return tenantApiFetch("/api/v1/widget-generator/settings", { method: "GET" });
}

/**
 * Replace the entire `widget_defaults` JSONB.
 * @param {{ widget_defaults: Record<string, string | number | boolean | null> }} body
 */
export async function putWidgetDefaults(body) {
  return tenantApiFetch("/api/v1/widget-generator/settings/widget-defaults", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/**
 * Merge and/or remove `widget_defaults` keys in a single transaction.
 * @param {{ set?: Record<string, string | number | boolean | null>, unset?: string[] }} body
 */
export async function patchWidgetDefaults(body) {
  return tenantApiFetch("/api/v1/widget-generator/settings/widget-defaults", {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function getEmbedContext() {
  return tenantApiFetch("/api/v1/embed/context", { method: "GET" });
}

/**
 * Build the embeddable `<script>` snippet. The workspace is taken from the
 * `X-Workspace-Id` header; do not pass `workspace_id` in the body.
 *
 * @param {{ overrides?: Record<string, unknown>, widget_url?: string, embed_js_url?: string }} [body]
 */
export async function postEmbedSnippet(body = {}) {
  return tenantApiFetch("/api/v1/embed/snippet", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Build a native-app (Android/iOS/Windows) WebView code snippet. The workspace is
 * taken from the `X-Workspace-Id` header. `embedder_origin` must already be registered.
 *
 * @param {{ platform: "android" | "ios" | "windows", embedder_origin: string, overrides?: Record<string, unknown>, widget_url?: string }} body
 */
export async function postEmbedNativeSnippet(body) {
  return tenantApiFetch("/api/v1/embed/native-snippet", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getEmbedCorsOrigins() {
  return tenantApiFetch("/api/v1/embed/cors-origins", { method: "GET" });
}

/**
 * @param {{ origin: string }} body
 */
export async function postEmbedCorsOrigin(body) {
  return tenantApiFetch("/api/v1/embed/cors-origins", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * @param {{ origin: string }} body
 */
export async function deleteEmbedCorsOrigin(body) {
  return tenantApiFetch("/api/v1/embed/cors-origins", {
    method: "DELETE",
    body: JSON.stringify(body),
  });
}
